import { useState, useEffect } from 'react';
import { Package, Phone, Clock, CheckCircle, XCircle, Truck, Loader2, Gift, MapPin } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface OrderItem {
  id: string;
  product_name: string;
  quantity: number;
  price_at_order: number;
  unit: string | null;
}

interface Order {
  id: string;
  status: string;
  total: number;
  discount_amount: number;
  applied_promocode: string | null;
  bonus_used: number;
  referral_used: number;
  created_at: string;
  notes: string | null;
  contact_phone: string | null;
  wants_delivery: boolean;
  delivery_cost: number;
  delivery_address: string | null;
}

interface DeliveryAddress {
  city?: string;
  street?: string;
  house?: string;
}

interface OrderDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: string | null;
}

const statusConfig: Record<string, { label: string; icon: React.ElementType; color: string; bgColor: string }> = {
  pending: { label: 'В обработке', icon: Clock, color: 'text-orange-500', bgColor: 'bg-orange-500/10' },
  processing: { label: 'Готовится', icon: Truck, color: 'text-yellow-500', bgColor: 'bg-yellow-500/10' },
  completed: { label: 'Выполнен', icon: CheckCircle, color: 'text-eco', bgColor: 'bg-eco/10' },
  cancelled: { label: 'Отменён', icon: XCircle, color: 'text-destructive', bgColor: 'bg-destructive/10' },
};

export const OrderDetailsModal = ({ open, onOpenChange, orderId }: OrderDetailsModalProps) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && orderId) {
      fetchOrderDetails();
    }
  }, [open, orderId]);

  const fetchOrderDetails = async () => {
    if (!orderId) return;

    setLoading(true);
    try {
      // Fetch order items for display
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select('id, product_name, quantity, price_at_order, unit')
        .eq('order_id', orderId);

      if (itemsError) throw itemsError;
      setOrderItems(itemsData || []);

      // Fetch order metadata including discount
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('id, status, total, discount_amount, applied_promocode, bonus_used, referral_used, created_at, notes, contact_phone, wants_delivery, delivery_cost, delivery_address')
        .eq('id', orderId)
        .maybeSingle();

      if (orderError) throw orderError;
      
      // Use total from database directly - it already has discounts applied by trigger
      if (orderData) {
        const discountAmount = Number(orderData.discount_amount) || 0;
        const bonusUsed = Number(orderData.bonus_used) || 0;
        // Cast to any because referral_used might not be in generated types yet
        const orderWithReferral = orderData as { referral_used?: number };
        const referralUsed = Number(orderWithReferral.referral_used) || 0;
        const deliveryCost = Number(orderData.delivery_cost) || 0;
        const finalTotal = Math.max(0, Number(orderData.total) || 0);
        
        logger.debug('Order details loaded:', { 
          orderId, 
          referral_used: orderWithReferral.referral_used,
          referralUsed,
          bonusUsed,
          discountAmount 
        });
        
        setOrder({ 
          ...orderData, 
          total: finalTotal, 
          discount_amount: discountAmount,
          bonus_used: bonusUsed,
          referral_used: referralUsed,
          delivery_cost: deliveryCost,
          wants_delivery: orderData.wants_delivery || false,
          delivery_address: orderData.delivery_address || null,
          applied_promocode: orderData.applied_promocode || null,
        });
      } else {
        setOrder(null);
      }
    } catch (error) {
      logger.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const status = order ? statusConfig[order.status] || statusConfig.pending : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] bg-card border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Заказ #{orderId?.slice(0, 8)}
          </DialogTitle>
          <DialogDescription>
            Детали вашего заказа
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : order ? (
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-4 pr-4">
              {/* Order Status and Date */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {new Date(order.created_at).toLocaleString('ru-RU', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </div>
                {status && (
                  <Badge className={`${status.bgColor} ${status.color} border-0`}>
                    {status.label}
                  </Badge>
                )}
              </div>

              <Separator />

              {/* Order Items */}
              <div>
                <h4 className="font-medium mb-3">Товары в заказе</h4>
                {orderItems.length === 0 ? (
                  <p className="text-muted-foreground text-sm">Нет товаров</p>
                ) : (
                  <div className="space-y-2">
                    {orderItems.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div>
                          <p className="font-medium text-sm">{item.product_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.quantity} × {item.price_at_order.toLocaleString('ru-RU')} ₽
                            {item.unit && ` / ${item.unit}`}
                          </p>
                        </div>
                        <p className="font-bold text-primary">
                          {(item.quantity * item.price_at_order).toLocaleString('ru-RU')} ₽
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Separator />

              {/* Contact Phone */}
              {order.contact_phone && (
                <>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">
                      Телефон: <a href={`tel:${order.contact_phone}`} className="text-primary hover:underline">{order.contact_phone}</a>
                    </span>
                  </div>
                  <Separator />
                </>
              )}

              {/* Notes */}
              {order.notes && (
                <>
                  <div>
                    <h4 className="font-medium mb-2">Примечание</h4>
                    <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
                      {order.notes}
                    </p>
                  </div>
                  <Separator />
                </>
              )}

              {/* Delivery Info */}
              {order.wants_delivery && (
                <>
                  <div className="p-3 bg-secondary/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium text-sm">Доставка</span>
                      {order.delivery_cost > 0 ? (
                        <Badge variant="secondary" className="ml-auto">+{order.delivery_cost.toLocaleString('ru-RU')} ₽</Badge>
                      ) : (
                        <Badge className="ml-auto bg-eco/10 text-eco border-0">Бесплатно</Badge>
                      )}
                    </div>
                    {order.delivery_address && (() => {
                      try {
                        const address: DeliveryAddress = JSON.parse(order.delivery_address);
                        const addressParts = [address.city, address.street, address.house].filter(Boolean);
                        if (addressParts.length > 0) {
                          return (
                            <div className="flex items-start gap-2 text-sm text-muted-foreground">
                              <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <span>{addressParts.join(', ')}</span>
                            </div>
                          );
                        }
                        return null;
                      } catch {
                        return null;
                      }
                    })()}
                  </div>
                  <Separator />
                </>
              )}

              {/* Discount */}
              {order.discount_amount > 0 && (
                <div className="flex items-center justify-between py-2 text-eco">
                  <span className="font-medium">
                    Скидка{order.applied_promocode && ` (${order.applied_promocode})`}:
                  </span>
                  <span className="font-semibold">
                    −{order.discount_amount.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              )}

              {/* Bonus Used */}
              {order.bonus_used > 0 && (
                <div className="flex items-center justify-between py-2 text-primary">
                  <span className="font-medium flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    Оплачено бонусами:
                  </span>
                  <span className="font-semibold">
                    −{order.bonus_used.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              )}

              {/* Referral Used */}
              {order.referral_used > 0 && (
                <div className="flex items-center justify-between py-2 text-amber-500">
                  <span className="font-medium flex items-center gap-2">
                    <Gift className="h-4 w-4" />
                    Реферальные бонусы:
                  </span>
                  <span className="font-semibold">
                    −{order.referral_used.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              )}

              {/* Delivery cost in summary */}
              {order.wants_delivery && order.delivery_cost > 0 && (
                <div className="flex items-center justify-between py-2">
                  <span className="font-medium flex items-center gap-2">
                    <Truck className="h-4 w-4 text-muted-foreground" />
                    Доставка:
                  </span>
                  <span className="font-semibold">
                    +{order.delivery_cost.toLocaleString('ru-RU')} ₽
                  </span>
                </div>
              )}

              {/* Total */}
              <div className="flex items-center justify-between py-2">
                <span className="font-medium">Итого:</span>
                <span className="text-xl font-bold text-primary">
                  {order.total.toLocaleString('ru-RU')} ₽
                </span>
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Заказ не найден
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
